import React from "react";
import { InlineMath } from "react-katex";
import { makeClosedTask, makeOptions, normalizeMay2026Variant, pick } from "../../../../matura/MaturaMay2026ClosedTaskSupport";

function toPolishNumberLatex(value) {
  return String(Math.round(value * 100) / 100).replace(".", "{,}");
}

const PERIOD_LABELS = {
  2: "dwuletnią",
  3: "trzyletnią",
  4: "czteroletnią",
};
const YEAR_LABELS = { 2: "dwóch", 3: "trzech", 4: "czterech" };

export function task02(variant) {
  const original = {
    capital: 10000,
    rate: 6,
    years: 2,
    options: [["A", "1200\\text{ zł}"], ["B", "1236\\text{ zł}"], ["C", "1836\\text{ zł}"], ["D", "3600\\text{ zł}"]],
  };
  const variants = [
    { capital: 5000, rate: 4, years: 2 },
    { capital: 8000, rate: 5, years: 2 },
    { capital: 10000, rate: 6, years: 2 },
    { capital: 12500, rate: 4, years: 2 },
    { capital: 8000, rate: 5, years: 3 },
    { capital: 15625, rate: 4, years: 3 },
    { capital: 125000, rate: 6, years: 3 },
    { capital: 160000, rate: 5, years: 4 },
    { capital: 320000, rate: 5, years: 4 },
  ];
  const item = variant === "original" ? original : pick(variants);
  return buildCompoundInterestTask(item, variant);
}

export function buildCompoundInterestTask(item, variant='random', taskNumber='2') {
  const finalCapital = Math.round(item.capital * (1 + item.rate / 100) ** item.years);
  const interest = finalCapital - item.capital;
  const firstYearInterest = item.capital * item.rate / 100;
  const simpleInterest = firstYearInterest * item.years;
  const doubleCountedFirstYearInterest = interest + firstYearInterest;
  const ckeRateMultiplierError = firstYearInterest * item.rate;
  const correct = toPolishNumberLatex(interest) + "\\text{ zł}";
  const wrong = [
    toPolishNumberLatex(simpleInterest) + "\\text{ zł}",
    toPolishNumberLatex(doubleCountedFirstYearInterest) + "\\text{ zł}",
    toPolishNumberLatex(ckeRateMultiplierError) + "\\text{ zł}",
  ];
  const { options, correctIndex } = item.options
    ? makeOptions(correct, [], item.options)
    : makeOptions(correct, wrong);
  return makeClosedTask({
    type: "MaturaCompoundInterestClosedTask",
    taskNumber,
    variant,
    tags: ["procent skladany"],
    data: {
      kind: "compoundInterest",
      capital: item.capital,
      rate: item.rate,
      years: item.years,
      interest,
      firstYearInterest,
      simpleInterest,
      doubleCountedFirstYearInterest,
      ckeRateMultiplierError,
      instruction: "Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.",
      prompt: <>Klient wpłacił do banku <InlineMath math={`${item.capital}\\text{ zł}`} /> na lokatę {PERIOD_LABELS[item.years]}. Po każdym rocznym okresie oszczędzania bank dolicza odsetki w wysokości <InlineMath math={`${item.rate}\\%`} /> od kwoty bieżącego kapitału znajdującego się na lokacie — zgodnie z procentem składanym.</>,
      afterLatex: `Po ${YEAR_LABELS[item.years]} latach oszczędzania łączna wartość doliczonych odsetek na tej lokacie (bez uwzględniania podatków) jest równa`,
      options,
      correctIndex,
      explanationSteps: [
        { title: <>Zapisujemy wzór na kapitał po <InlineMath math={String(item.years)} /> latach.</>, latex: `K_${item.years}=${item.capital}\\left(1+\\frac{${item.rate}}{100}\\right)^${item.years}` },
        { title: <>Obliczamy wartość lokaty po <InlineMath math={String(item.years)} /> kapitalizacjach.</>, latex: `K_${item.years}=${toPolishNumberLatex(finalCapital)}\\text{ zł}` },
        { title: "Obliczamy same odsetki, bez uwzględniania podatków: od końcowego kapitału odejmujemy wpłaconą kwotę.", latex: `\\text{odsetki}=K_${item.years}-K_0=${toPolishNumberLatex(finalCapital)}-${item.capital}=${toPolishNumberLatex(interest)}\\text{ zł}` },
      ],
    },
  });
}

export function generateTask(options = {}) {
  if(options.variant==='original'&&options.session==='2023_CZE'){
    const task=buildCompoundInterestTask({capital:30000,rate:7,years:2,options:[['A','2100\\text{ zł}'],['B','2247\\text{ zł}'],['C','4200\\text{ zł}'],['D','4347\\text{ zł}']]},'original','4');
    return {...task,source:{...task.source,session:'2023_CZE',taskNumber:'4'}};
  }
  return task02(normalizeMay2026Variant(options));
}

export { checkClosedAnswer as checkAnswer, renderClosedAnswer as renderAnswer, renderClosedExplanation as renderExplanation, renderClosedInput as renderInput, renderClosedTask as renderTask, renderClosedUserAnswer as renderUserAnswer } from "../../../../matura/MaturaMay2026ClosedTaskSupport";
